package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(Report.Companion.Serializer::class)
public class Report : Resource, ReportSchema {
  override val `companion`: Resource.CompanionObj<Report> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var reportableType: String
    get() = if (attributes.containsKey("reportable_type")) {
      attributes["reportable_type"] as String
    } else {
      throw NoSuchElementException("reportableType not found")
    }
    set(`value`) {
      attributes["reportable_type"] = value
    }

  override var reportableId: String
    get() = if (attributes.containsKey("reportable_id")) {
      attributes["reportable_id"] as String
    } else {
      throw NoSuchElementException("reportableId not found")
    }
    set(`value`) {
      attributes["reportable_id"] = value
    }

  override var reason: String
    get() = if (attributes.containsKey("reason")) {
      attributes["reason"] as String
    } else {
      throw NoSuchElementException("reason not found")
    }
    set(`value`) {
      attributes["reason"] = value
    }

  override var note: String?
    get() = if (attributes.containsKey("note")) {
      attributes["note"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["note"] = value
    }

  public constructor(
    reportableType: String? = null,
    reportableId: String? = null,
    reason: String? = null,
    note: String? = null,
  ) {
    if (reportableType != null) {
      attributes["reportable_type"] = reportableType
    }
    if (reportableId != null) {
      attributes["reportable_id"] = reportableId
    }
    if (reason != null) {
      attributes["reason"] = reason
    }
    if (note != null) {
      attributes["note"] = note
    }
  }

  public companion object : Resource.CompanionObj<Report> {
    override val resourceType: String = "Report"

    override val endpoint: String = "reports"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> Report = { Report() }

    override val schema: KClass<ReportSchema> = ReportSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<Report>.() -> Unit = {}): Scope<Report> = Scope(Report::class, options)

    public final suspend fun all(): CollectionProxy<Report> = scope().all()

    public final suspend fun find(id: String): RecordProxy<Report> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<Report?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<Report> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<Report?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<Report> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<Report?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<Report> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<Report> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<Report> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<Report> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<Report> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<Report> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<Report> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<Report> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<Report> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<Report> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<Report>()
  }
}
